import { useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast from "react-hot-toast";
import {
  CheckCircleIcon,
  EllipsisVerticalIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

const EditableCell = ({
  value,
  onSave,
  placeholder = "—",
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value || "");

  useEffect(() => setTemp(value || ""), [value]);

  const handleSave = () => {
    const trimmed = temp.trim();
    if (trimmed !== value && !disabled) onSave(trimmed);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        className={`px-3 py-2.5 rounded-lg border border-transparent min-h-[2.8rem] flex items-center text-xs sm:text-sm font-medium text-blue-800 transition-all ${
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:border-blue-400 hover:bg-blue-50/70"
        }`}
        title={
          disabled
            ? "You do not have permission to edit distributor"
            : undefined
        }
      >
        {value || <span className="text-blue-400">{placeholder}</span>}
      </div>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      value={temp}
      onChange={(e) => setTemp(e.target.value)}
      onBlur={handleSave}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") handleSave();
      }}
      onKeyUp={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") setEditing(false);
      }}
      disabled={disabled}
      className={`w-full px-3 py-2 rounded-lg border-2 text-sm font-medium text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
        disabled
          ? "bg-gray-100 cursor-not-allowed opacity-60"
          : "bg-white border-blue-500"
      }`}
      placeholder={placeholder}
    />
  );
};

const EditableHeader = ({ value, onSave, disabled = false }) => {
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value);

  useEffect(() => setTemp(value), [value]);

  const handleSave = () => {
    const trimmed = temp.trim();
    if (trimmed && trimmed !== value && !disabled) {
      onSave(trimmed);
      toast.success("Column header renamed!", { position: "top-right" });
    }
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        onClick={() => !disabled && setEditing(true)}
        className={`text-gray-900 px-2 py-1 rounded font-bold uppercase text-xs tracking-wider flex items-center gap-1 group ${
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:bg-white/10"
        }`}
        title={
          disabled ? "You do not have permission to rename columns" : undefined
        }
      >
        {value || "Click to name"}
        {!disabled && (
          <PencilIcon className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" />
        )}
      </div>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      value={temp}
      onChange={(e) => setTemp(e.target.value)}
      onBlur={handleSave}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleSave();
        if (e.key === "Escape") setEditing(false);
      }}
      disabled={disabled}
      className={`w-28 px-2 py-1 text-xs font-bold text-gray-900 bg-white rounded border ${
        disabled ? "border-gray-300 opacity-60" : "border-blue-500"
      } focus:outline-none`}
      placeholder="Name..."
    />
  );
};

const BreakBadge = ({ breakTimeAdded, breaksIncluded = [], breakSummary }) => {
  if (!breakTimeAdded || breakTimeAdded <= 0) return null;

  const firstBreakLabel = breaksIncluded?.[0]?.label?.toLowerCase() || "";

  const isMorning = firstBreakLabel.includes("morning");
  const isLunch = firstBreakLabel.includes("lunch");
  const isAfternoon = firstBreakLabel.includes("afternoon");

  // Clean UI-friendly style map
  const styles = {
    morning: {
      bg: "bg-yellow-100",
      text: "text-yellow-800",
      border: "border-yellow-300",
      name: "Morning Break",
      icon: "☀️",
    },
    lunch: {
      bg: "bg-orange-100",
      text: "text-orange-800",
      border: "border-orange-300",
      name: "Lunch Break",
      icon: "🍽️",
    },
    default: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      border: "border-amber-300",
      name: "Break",
      icon: "⏱️",
    },
  };

  const selectedStyle = isLunch
    ? styles.lunch
    : isMorning
      ? styles.morning
      : styles.default;

  const breakType = selectedStyle.name.toLowerCase();
  const tooltip = breakSummary || `Includes ${breakTimeAdded}m ${breakType}`;

  return (
    <span
      title={tooltip}
      className={`
        inline-flex items-center gap-1 ml-2
        px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold
        border whitespace-nowrap
        ${selectedStyle.bg} ${selectedStyle.text} ${selectedStyle.border}
      `}
    >
      <span className="text-[12px]">{selectedStyle.icon}</span>
      {selectedStyle.name}: {breakTimeAdded}m
    </span>
  );
};

export const SortableRow = ({
  id,
  wo,
  getStatusIcon,
  room,
  isReadOnly,
  canPlaceOnHold,
  canUnassign,
  onContextMenuAction,
  handleHoldAction,
  handleComplete,
  extraColumns,
  getCellValue,
  updateCellValue,
  onlineValues,
  updateOnlineValue,
  packingValues,
  updatePackingValue,
  selectedWorkOrders,
  handleSelectWorkOrder,
  canEditDistributors,
  onOpenActionMenu,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: isDragging ? "#f0f9ff" : "white",
  };

  const isCompleted =
    wo.status === "completed" || wo.markedAsCompletedLocally === true;
  const isOnHold = ["LAB HOLD", "QC HOLD"].includes(wo.warehouse || "");

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...(wo.isSplitCopy ? {} : attributes)}
      {...(wo.isSplitCopy ? {} : listeners)}
      data-row-id={wo._id}
      data-row-type="workorder"
      data-row-draggable="true"
      className={`group transition-all duration-200 ${
        isDragging
          ? "shadow-2xl ring-2 ring-blue-400 z-10 scale-[1.02]"
          : wo.isSplitCopy 
            ? "hover:bg-gray-50" 
            : "hover:bg-blue-50/50 cursor-grab active:cursor-grabbing"
      }`}
    >
      {/* Checkbox */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm">
        {wo.isTransferred ? (
          <div className="flex items-center justify-center h-6 w-6">
            <CheckCircleIcon
              className="h-5 w-5 text-green-600"
              aria-label="Transferred"
            />
          </div>
        ) : (
          <input
            type="checkbox"
            checked={selectedWorkOrders.has(wo.woNumber)}
            onChange={() => handleSelectWorkOrder(wo.woNumber)}
            disabled={isReadOnly || !isCompleted || isOnHold}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
        )}
      </td>

      {/* Slot / Time */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm text-green-700 font-medium">
        {wo.slot || "Not Scheduled"}
        {wo.totalDurationMinutes ? ` (${wo.totalDurationMinutes} min)` : ""}
        {wo.breakTimeAdded > 0 && (
          <BreakBadge
            breakTimeAdded={wo.breakTimeAdded}
            breaksIncluded={wo.breaksIncluded}
            breakSummary={wo.breakSummary}
          />
        )}
      </td>

      {/* WO Number */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        <div className="flex items-center gap-1">
          {wo.woNumber}
          {wo.isSplitCopy && (
            <span title="Split Copy" className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 whitespace-nowrap">
              ↳ Split
            </span>
          )}
        </div>
      </td>

      {/* Project # */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.projectNumber}
      </td>

      {/* SKU */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.sku || "N/A"}
      </td>

      {/* Title */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.title}
      </td>

      {/* Label */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.labelCategory || "N/A"}
      </td>

      {/* Allergen */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.allergen || "None"}
      </td>

      {/* Net Weight */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.netWeight || "N/A"}
      </td>

      {/* Planned Qty */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        <div>{wo.plannedQty || 0}</div>
        <div className="text-[11px] text-gray-500">
          Initial: {wo.kpiPlannedQty ?? wo.plannedQty ?? 0}
        </div>
        <div className="text-[11px] text-gray-500">
          Final: {wo.currentErpagQty ?? wo.plannedQty ?? 0}
        </div>
      </td>

      {/* Actual Qty */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.actualQty || 0}
      </td>

      {/* Status */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm">
        {getStatusIcon(wo.status)}
      </td>

      {/* Lot Number */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.lotNumber || "N/A"}
      </td>

      {/* Assigned Date */}
      <td className="py-3 px-3 border-b border-gray-200 text-xs sm:text-sm select-text">
        {wo.assignedDate || "N/A"}
      </td>

      {/* Extra Columns */}
      {extraColumns.map((col) => (
        <td key={col.key} className="py-2.5 px-3 border-b border-gray-200">
          <EditableCell
            value={getCellValue(wo._id, col.key)}
            onSave={(v) => updateCellValue(wo._id, col.key, v)}
            placeholder="—"
            disabled={!canEditDistributors}
          />
        </td>
      ))}

      {/* Online Qty */}
      <td className="py-2.5 px-3 border-b border-gray-200">
        <EditableCell
          value={onlineValues[wo._id] || ""}
          onSave={(v) => updateOnlineValue(wo._id, v)}
          placeholder="—"
          disabled={!canEditDistributors || isReadOnly}
        />
      </td>

      {/* Packing */}
      <td className="py-2.5 px-3 border-b border-gray-200">
        <EditableCell
          value={packingValues[wo._id] || ""}
          onSave={(v) => updatePackingValue(wo._id, v)}
          placeholder="-"
          disabled={!canEditDistributors || isReadOnly}
        />
      </td>

      {/* Actions Column */}
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => onOpenActionMenu?.(event, wo)}
          className="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
          title="Work order actions"
          aria-label={`Open actions for work order ${wo.woNumber}`}
        >
          <EllipsisVerticalIcon className="h-5 w-5" />
        </button>

        {isCompleted && !isOnHold && !isReadOnly && !wo.isTransferred && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleHoldAction(wo, "LAB_HOLD")}
              className="py-1 px-2 rounded bg-[#fef08a] hover:bg-[#facc15] text-gray-900 text-xs font-semibold transition"
              title="Place on LAB HOLD"
            >
              LAB HOLD
            </button>
            <button
              onClick={() => handleHoldAction(wo, "QC_HOLD")}
              className="py-1 px-2 rounded bg-[#f97316] hover:bg-[#ea580c] text-gray-900 text-xs font-semibold transition"
              title="Place on QC HOLD"
            >
              QC HOLD
            </button>
          </div>
        )}

        {wo.isTransferred && (
          <div className="flex justify-end">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Transferred
            </span>
          </div>
        )}

        {!isCompleted && !isOnHold && !isReadOnly && (
          <button
            onClick={() => handleComplete(wo)}
            className="py-1 px-2 rounded bg-[#6ca351] hover:bg-green-700 text-white text-xs font-semibold transition mt-1 block"
            title="Mark as Completed in Planipro"
          >
            Complete
          </button>
        )}

        {wo.room &&
          wo.status !== "completed" &&
          !wo.markedAsCompletedLocally &&
          !isOnHold &&
          !isReadOnly &&
          canUnassign && (
            <button
              onClick={() => onContextMenuAction?.(wo, "unassign")}
              className="py-1 px-2 rounded bg-black hover:bg-gray-900 text-white text-xs font-semibold mt-1 block"
            >
              Unassign
            </button>
          )}

        {isOnHold && (
          <div className="mt-2 flex justify-center">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full
                bg-${wo.warehouse === "LAB HOLD" ? "yellow" : "orange"}-100
                text-${wo.warehouse === "LAB HOLD" ? "yellow" : "orange"}-800
                border border-${wo.warehouse === "LAB HOLD" ? "yellow" : "orange"}-300`}
            >
              ON {wo.warehouse}
            </span>
          </div>
        )}

        {wo.type === "workorder" &&
          !wo.sku &&
          !isCompleted &&
          !isOnHold &&
          !isReadOnly && (
            <button
              onClick={() => onContextMenuAction?.(wo, "removeTask", null)}
              className="py-1 px-2 rounded bg-red-200 hover:bg-red-300 text-gray-900 text-xs font-semibold"
            >
              Remove
            </button>
          )}
      </td>
    </tr>
  );
};

export const LegacySortableEventRow = ({
  id,
  event,
  isReadOnly,
  onRemoveCleaningTask,
  onRenameCleaningTask,
  extraColumns = [],
  getStatusIcon,
  packingValues = {},
  updatePackingValue,
  canEditDistributors = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: isDragging ? "#f8fafc" : undefined,
  };

  const isBreak = event.type?.includes("break");
  const isCustomTask =
    event.isCustomTask === true ||
    String(event.type || "").toLowerCase() === "custom";

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-row-id={event._id}
      data-row-type="cleaning"
      data-row-draggable="true"
      className={`p-3 rounded-lg shadow-sm border-2 transition-all duration-200 ${
        isCustomTask
          ? "bg-purple-50 border-purple-200 text-purple-900 font-medium shadow-sm ring-1 ring-purple-100"
          : isBreak
          ? "bg-amber-50 border-amber-400 text-amber-900 font-medium shadow-md"
          : "bg-gray-50 text-gray-700 italic border-gray-300"
      } ${isDragging ? "ring-2 ring-blue-400" : ""}`}
    >
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm" />
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-green-700 font-medium">
        {event.slot} ({event.duration} min)
        {event.breakTimeAdded > 0 && (
          <BreakBadge
            breakTimeAdded={event.breakTimeAdded}
            breaksIncluded={event.breaksIncluded}
            breakSummary={event.breakSummary}
          />
        )}
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        {isCustomTask ? (
          <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-purple-800">
            Custom Task
          </span>
        ) : (
          event.title
        )}
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
        {getStatusIcon ? getStatusIcon(event.status || "-") : "-"}
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        — 
      </td>
      {extraColumns.map((col) => (
        <td key={col.key} className="py-2.5 px-3 border-b border-gray-200">
          <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
            — 
          </div>
        </td>
      ))}
      <td className="py-2.5 px-3 border-b border-gray-200">
        <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
          — 
        </div>
      </td>
      <td className="py-2.5 px-3 border-b border-gray-200">
        <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
          — 
        </div>
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
        {event.type === "manual cleaning" &&
          event.isCustomTask &&
          onRenameCleaningTask &&
          !isReadOnly && (
            <button
              onClick={() =>
                onRenameCleaningTask(event._id, event.title || "Custom Task")
              }
              className="px-2 py-1 text-xs rounded bg-blue-800 text-white hover:bg-blue-700 mr-2"
            >
              Rename
            </button>
          )}
        {event.type === "manual cleaning" &&
          onRemoveCleaningTask &&
          !isReadOnly && (
            <button
              onClick={() => onRemoveCleaningTask(event._id)}
              className="px-2 py-1 text-xs rounded bg-red-800 text-white hover:bg-red-600 mt-1"
            >
              Remove
            </button>
          )}
      </td>
    </tr>
  );
};

const DashCell = ({ children = "-" }) => (
  <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
    {children}
  </td>
);

export const SortableEventRow = ({
  id,
  event,
  isReadOnly,
  onRemoveCleaningTask,
  onRenameCleaningTask,
  extraColumns = [],
  getStatusIcon,
  packingValues = {},
  updatePackingValue,
  canEditDistributors = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: isDragging ? "#f8fafc" : undefined,
  };

  const isBreak = event.type?.includes("break");
  const isCustomTask =
    event.isCustomTask === true ||
    String(event.type || "").toLowerCase() === "custom";
  const canEditPacking =
    isCustomTask && Boolean(updatePackingValue) && canEditDistributors && !isReadOnly;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-row-id={event._id}
      data-row-type="cleaning"
      data-row-draggable="true"
      className={`p-3 rounded-lg shadow-sm border-2 transition-all duration-200 ${
        isCustomTask
          ? "bg-purple-50 border-purple-200 text-purple-900 font-medium shadow-sm ring-1 ring-purple-100"
          : isBreak
            ? "bg-amber-50 border-amber-400 text-amber-900 font-medium shadow-md"
            : "bg-gray-50 text-gray-700 italic border-gray-300"
      } ${isDragging ? "ring-2 ring-blue-400" : ""}`}
    >
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm" />
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm text-green-700 font-medium">
        {event.slot} ({event.duration} min)
        {event.breakTimeAdded > 0 && (
          <BreakBadge
            breakTimeAdded={event.breakTimeAdded}
            breaksIncluded={event.breaksIncluded}
            breakSummary={event.breakSummary}
          />
        )}
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        {isCustomTask ? (
          <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-purple-800">
            Custom Task
          </span>
        ) : (
          event.title
        )}
      </td>
      <DashCell />
      <DashCell />
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-md sm:text-sm select-text">
        {isCustomTask ? (
          <div className="min-w-[180px]">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-600">
              Custom Task
            </div>
            <div className="text-sm font-semibold text-purple-900">
              {event.title || "Custom Task"}
            </div>
          </div>
        ) : (
          "-"
        )}
      </td>
      <DashCell />
      <DashCell />
      <DashCell />
      <DashCell />
      <DashCell />
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
        {getStatusIcon ? getStatusIcon(event.status || "-") : "-"}
      </td>
      <DashCell />
      <DashCell />
      {extraColumns.map((col) => (
        <td key={col.key} className="py-2.5 px-3 border-b border-gray-200">
          <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
            -
          </div>
        </td>
      ))}
      <td className="py-2.5 px-3 border-b border-gray-200">
        <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
          -
        </div>
      </td>
      <td className="py-2.5 px-3 border-b border-gray-200">
        {isCustomTask ? (
          <EditableCell
            value={packingValues[event._id] || ""}
            onSave={(v) => updatePackingValue?.(event._id, v)}
            placeholder="Add packing / notes"
            disabled={!canEditPacking}
          />
        ) : (
          <div className="px-3 py-2.5 text-xs sm:text-sm text-gray-500 italic min-h-[2.8rem] flex items-center">
            -
          </div>
        )}
      </td>
      <td className="py-2 sm:py-3 px-2 sm:px-4 border-b border-gray-200 text-xs sm:text-sm">
        {event.type === "manual cleaning" &&
          event.isCustomTask &&
          onRenameCleaningTask &&
          !isReadOnly && (
            <button
              onClick={() =>
                onRenameCleaningTask(event._id, event.title || "Custom Task")
              }
              className="px-2 py-1 text-xs rounded bg-blue-800 text-white hover:bg-blue-700 mr-2"
            >
              Rename
            </button>
          )}
        {event.type === "manual cleaning" &&
          onRemoveCleaningTask &&
          !isReadOnly && (
            <button
              onClick={() => onRemoveCleaningTask(event._id)}
              className="px-2 py-1 text-xs rounded bg-red-800 text-white hover:bg-red-600 mt-1"
            >
              Remove
            </button>
          )}
      </td>
    </tr>
  );
};
